import { Injectable } from '@angular/core';
import { environment } from 'projects/sharing/environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { API } from '../configs/api.config';
import { EMAIL } from '../configs/email.config';
import { LS } from '../enums/local-storage.enum';
import {
  ChangeEmailFlow,
  ChangePasswordFlow,
  ClaimOTPType,
} from '../enums/user-profile.enum';
import { CheckEmailInput, CheckEmailOutput } from '../models/auth.model';
import { SendEmailInput } from '../models/email.model';
import { ResponseDataType } from '../models/general.model';
import {
  ChangeEmailInput,
  ChangePasswordInput,
  LoginSessionInput,
  LoginSessionOutput,
  LogoutByIDsInput,
  MFAnOTPChangeEmailForLS,
  MFAnOTPChangeEmailForLSList,
  MFAnOTPChangePasswordForLS,
  MFAnOTPChangePasswordForLSList,
  MFAVerifyInput,
  MFAVerifyOutput,
  ProfileDetailInput,
  ProfileDetailOutput,
  SendOTPChangeEmailInput,
  SendOTPChangePasswordInput,
  SendOTPOutput,
  UserProfileOutput,
  VerifyOTPInput,
  VerifyOTPOutput,
} from '../models/user-profile.model';
import { GeneralService } from './general.service';

@Injectable({
  providedIn: 'root',
})
export class UserProfileService {
  actionId = environment.PROFILE_AND_ACCOUNT_SETTINGS_ACTION_ID;
  menuId = environment.PROFILE_AND_ACCOUNT_SETTINGS_MENU_ID;
  private readonly changeEmailBS = new BehaviorSubject<
    ChangeEmailInput | undefined
  >(undefined);
  public changeEmailBS$ = this.changeEmailBS.asObservable();
  private readonly changePasswordBS = new BehaviorSubject<
    ChangePasswordInput | undefined
  >(undefined);
  public changePasswordBS$ = this.changePasswordBS.asObservable();
  private RefreshHeader = new BehaviorSubject<boolean>(false);
  public RefreshHeader$ = this.RefreshHeader.asObservable();
  private readonly MFAUserProfile = new BehaviorSubject<
    MFAVerifyOutput | undefined
  >(undefined);
  public MFAUserProfile$ = this.MFAUserProfile.asObservable();
  private readonly MfaOtpChangeEmailMapKey = LS.MFA_OTP_CHANGE_EMAIL_MAP;
  private readonly MfaOtpChangePasswordMapKey = LS.MFA_OTP_CHANGE_PASSWORD_MAP;
  private MfaOtpChangeEmailMap: MFAnOTPChangeEmailForLSList = {};
  private MfaOtpChangePasswordMap: MFAnOTPChangePasswordForLSList = {};

  constructor(private readonly generalService: GeneralService) {
    this.initializeFromStorage();
  }

  private initializeFromStorage(): void {
    const storedMfaOtpChangeEmail = localStorage.getItem(
      this.MfaOtpChangeEmailMapKey,
    ) as string;
    if (this.generalService.isJSON(storedMfaOtpChangeEmail)) {
      this.MfaOtpChangeEmailMap = JSON.parse(storedMfaOtpChangeEmail);
    }
    const storedMfaOtpChangePassword = localStorage.getItem(
      this.MfaOtpChangePasswordMapKey,
    ) as string;
    if (this.generalService.isJSON(storedMfaOtpChangePassword)) {
      this.MfaOtpChangePasswordMap = JSON.parse(storedMfaOtpChangePassword);
    }
  }

  setRefreshHeader(item: boolean): void {
    this.RefreshHeader.next(item);
  }

  setChangeEmailBS(item: ChangeEmailInput | undefined): void {
    this.changeEmailBS.next(item);
  }

  setChangePasswordBS(item: ChangePasswordInput | undefined): void {
    this.changePasswordBS.next(item);
  }

  getChangeEmailBS(): ChangeEmailInput | undefined {
    return this.changeEmailBS.getValue();
  }

  getChangePasswordBS(): ChangePasswordInput | undefined {
    return this.changePasswordBS.getValue();
  }

  setMFAUserProfile(item: MFAVerifyOutput | undefined): void {
    this.MFAUserProfile.next(item);
  }

  getUserProfile(): Observable<ResponseDataType<UserProfileOutput>> {
    return this.generalService.postData(
      {},
      API.USER_SETTINGS_GET_USER_PROFILE,
      API.BASE_URL,
      this.menuId,
      this.actionId,
    );
  }

  getLoginSession(
    params: LoginSessionInput,
  ): Observable<ResponseDataType<LoginSessionOutput[]>> {
    return this.generalService.postData(
      params,
      API.USER_SETTINGS_GET_LOGIN_SESSIONS,
      API.BASE_URL,
      this.menuId,
      this.actionId,
    );
  }

  updateProfileDetails(
    params: ProfileDetailInput,
    mfaToken: string,
  ): Observable<ResponseDataType<ProfileDetailOutput>> {
    return this.generalService.postWithOTPnMFAToken(
      params,
      API.USER_SETTINGS_UPDATE_PROFILE_DETAILS,
      '-',
      mfaToken,
      API.BASE_URL,
      this.menuId,
      this.actionId,
    );
  }

  sendEmailChangeRequest(
    toEmail: string,
    mfaToken: string,
  ): Observable<ResponseDataType<boolean>> {
    const data: SendEmailInput = {
      toEmail,
      emailType: EMAIL.EMAIL_TYPE_EMAIL_CHANGE_REQUEST,
    };
    return this.generalService.postWithOTPnMFAToken(
      data,
      API.SEND_EMAIL,
      '-',
      mfaToken,
      API.EMAIL_BASE_URL,
      this.menuId,
      this.actionId,
    );
  }

  logoutByIDs(params: LogoutByIDsInput): Observable<ResponseDataType<boolean>> {
    return this.generalService.postData(
      params,
      API.USER_SETTINGS_LOGOUT_BY_IDS,
      API.AUTH_BASE_URL,
      this.menuId,
      this.actionId,
    );
  }

  sendOTPChangeEmail(
    email: string,
    confirmedPassword: string,
    mfaToken: string,
  ): Observable<ResponseDataType<SendOTPOutput>> {
    const data: SendOTPChangeEmailInput = {
      email,
      confirmedPassword,
      claim: ClaimOTPType.CHANGE_EMAIL,
    };
    return this.generalService.postWithOTPnMFAToken(
      data,
      API.USER_SETTINGS_SEND_OTP,
      '-',
      mfaToken,
      API.AUTH_BASE_URL,
      this.menuId,
      this.actionId,
    );
  }

  verifyOTPChangeEmail(
    otp: string,
    otpToken: string,
    mfaToken: string,
  ): Observable<ResponseDataType<VerifyOTPOutput>> {
    const data: VerifyOTPInput = {
      otp,
      type: ClaimOTPType.CHANGE_EMAIL,
    };
    return this.generalService.postWithOTPnMFAToken(
      data,
      API.USER_SETTINGS_VERIFY_OTP,
      otpToken,
      mfaToken,
      API.AUTH_BASE_URL,
      this.menuId,
      this.actionId,
    );
  }

  changeEmail(
    params: ChangeEmailInput,
    otpToken: string,
    mfaToken: string,
  ): Observable<ResponseDataType<boolean>> {
    return this.generalService.postWithOTPnMFAToken(
      params,
      API.USER_SETTINGS_CHANGE_EMAIL,
      otpToken,
      mfaToken,
      API.BASE_URL,
      this.menuId,
      this.actionId,
    );
  }

  changePassword(
    params: ChangePasswordInput,
    otpToken: string,
    mfaToken: string,
  ): Observable<ResponseDataType<boolean>> {
    return this.generalService.postWithOTPnMFAToken(
      params,
      API.USER_SETTINGS_CHANGE_PASSWORD,
      otpToken,
      mfaToken,
      API.BASE_URL,
      this.menuId,
      this.actionId,
    );
  }

  sendOTPChangePassword(
    email: string,
    mfaToken: string,
  ): Observable<ResponseDataType<SendOTPOutput>> {
    const data: SendOTPChangePasswordInput = {
      email,
      claim: ClaimOTPType.CHANGE_PASSWORD,
    };
    return this.generalService.postWithOTPnMFAToken(
      data,
      API.USER_SETTINGS_SEND_OTP,
      '-',
      mfaToken,
      API.AUTH_BASE_URL,
      this.menuId,
      this.actionId,
    );
  }

  verifyOTPChangePassword(
    otp: string,
    otpToken: string,
    mfaToken: string,
  ): Observable<ResponseDataType<VerifyOTPOutput>> {
    const data: VerifyOTPInput = {
      otp,
      type: ClaimOTPType.CHANGE_PASSWORD,
    };
    return this.generalService.postWithOTPnMFAToken(
      data,
      API.USER_SETTINGS_VERIFY_OTP,
      otpToken,
      mfaToken,
      API.AUTH_BASE_URL,
      this.menuId,
      this.actionId,
    );
  }

  mfaVerify(
    params: MFAVerifyInput,
  ): Observable<ResponseDataType<MFAVerifyOutput>> {
    return this.generalService.postData(
      params,
      API.USER_SETTINGS_MFA_VERIFY,
      API.AUTH_BASE_URL,
      this.menuId,
      this.actionId,
    );
  }

  setMFAnOTPChangeEmailForLS(
    email: string,
    item: MFAnOTPChangeEmailForLS,
  ): void {
    try {
      const storedData = localStorage.getItem(this.MfaOtpChangeEmailMapKey);
      if (storedData && this.generalService.isJSON(storedData)) {
        this.MfaOtpChangeEmailMap = JSON.parse(storedData);
      }
      this.MfaOtpChangeEmailMap[email] = item;
      localStorage.setItem(
        this.MfaOtpChangeEmailMapKey,
        JSON.stringify(this.MfaOtpChangeEmailMap),
      );
    } catch (error) {
      console.error(error);
      this.resetMFAnOTPChangeEmailForLS(email);
    }
  }

  private resetStateMFAnOTPChangeEmailForLS(): MFAnOTPChangeEmailForLS {
    const res: MFAnOTPChangeEmailForLS = {
      mfaToken: null,
      otpToken: null,
      mfaExpired: null,
      otpExpired: null,
      mfaDuration: null,
      otpDuration: null,
      flow: ChangeEmailFlow.MODAL_MFA,
    };
    return res;
  }

  private resetMFAnOTPChangeEmailForLS(email: string): void {
    const item: MFAnOTPChangeEmailForLSList = {
      [email]: this.resetStateMFAnOTPChangeEmailForLS(),
    };
    localStorage.setItem(this.MfaOtpChangeEmailMapKey, JSON.stringify(item));
  }

  setOnlyFlowMFAnOTPChangeEmailForLS(
    email: string,
    flow: ChangeEmailFlow,
  ): void {
    try {
      const data = this.getLSMFAnOTPChangeEmailByEmail(
        email,
      ) as MFAnOTPChangeEmailForLS;
      if (data) {
        data.flow = flow;
        this.MfaOtpChangeEmailMap[email] = data;
        localStorage.setItem(
          this.MfaOtpChangeEmailMapKey,
          JSON.stringify(this.MfaOtpChangeEmailMap),
        );
      }
    } catch (error) {
      console.error(error);
      this.resetMFAnOTPChangeEmailForLS(email);
    }
  }

  clearLSMFAnOTPChangeEmail(email: string): void {
    delete this.MfaOtpChangeEmailMap[email];
    localStorage.setItem(
      this.MfaOtpChangeEmailMapKey,
      JSON.stringify(this.MfaOtpChangeEmailMap),
    );
  }

  getLSMFAnOTPChangeEmailByEmail(
    email: string,
  ): MFAnOTPChangeEmailForLS | null {
    try {
      const storedMfaOtp = localStorage.getItem(this.MfaOtpChangeEmailMapKey);
      if (storedMfaOtp && this.generalService.isJSON(storedMfaOtp)) {
        this.MfaOtpChangeEmailMap = JSON.parse(storedMfaOtp) || {};
        const data = this.MfaOtpChangeEmailMap[email];
        if (data) {
          return data as MFAnOTPChangeEmailForLS;
        }
      }
      return null;
    } catch (error) {
      console.error(error);
      this.resetMFAnOTPChangeEmailForLS(email);
      return this.resetStateMFAnOTPChangeEmailForLS();
    }
  }

  setMFAnOTPChangePasswordForLS(
    email: string,
    item: MFAnOTPChangePasswordForLS,
  ): void {
    try {
      const storedData = localStorage.getItem(this.MfaOtpChangePasswordMapKey);
      if (storedData && this.generalService.isJSON(storedData)) {
        this.MfaOtpChangePasswordMap = JSON.parse(storedData);
      }
      this.MfaOtpChangePasswordMap[email] = item;
      localStorage.setItem(
        this.MfaOtpChangePasswordMapKey,
        JSON.stringify(this.MfaOtpChangePasswordMap),
      );
    } catch (error) {
      console.error(error);
      this.resetMFAnOTPChangePasswordForLS(email);
    }
  }

  private resetStateMFAnOTPChangePasswordForLS(): MFAnOTPChangePasswordForLS {
    const res: MFAnOTPChangePasswordForLS = {
      mfaToken: null,
      otpToken: null,
      mfaExpired: null,
      otpExpired: null,
      mfaDuration: null,
      otpDuration: null,
      flow: ChangePasswordFlow.MODAL_MFA,
    };
    return res;
  }

  private resetMFAnOTPChangePasswordForLS(email: string): void {
    const item: MFAnOTPChangePasswordForLSList = {
      [email]: this.resetStateMFAnOTPChangePasswordForLS(),
    };
    localStorage.setItem(this.MfaOtpChangePasswordMapKey, JSON.stringify(item));
  }

  setOnlyFlowMFAnOTPChangePasswordForLS(
    email: string,
    flow: ChangePasswordFlow,
  ): void {
    try {
      const data = this.getLSMFAnOTPChangePasswordByEmail(
        email,
      ) as MFAnOTPChangePasswordForLS;
      if (data) {
        data.flow = flow;
        this.MfaOtpChangePasswordMap[email] = data;
        localStorage.setItem(
          this.MfaOtpChangePasswordMapKey,
          JSON.stringify(this.MfaOtpChangePasswordMap),
        );
      }
    } catch (error) {
      console.error(error);
      this.resetMFAnOTPChangePasswordForLS(email);
    }
  }

  clearLSMFAnOTPChangePassword(email: string): void {
    delete this.MfaOtpChangePasswordMap[email];
    localStorage.setItem(
      this.MfaOtpChangePasswordMapKey,
      JSON.stringify(this.MfaOtpChangePasswordMap),
    );
  }

  getLSMFAnOTPChangePasswordByEmail(
    email: string,
  ): MFAnOTPChangePasswordForLS | null {
    try {
      const storedMfaOtp = localStorage.getItem(
        this.MfaOtpChangePasswordMapKey,
      );
      if (storedMfaOtp && this.generalService.isJSON(storedMfaOtp)) {
        this.MfaOtpChangePasswordMap = JSON.parse(storedMfaOtp) || {};
        const data = this.MfaOtpChangePasswordMap[email];
        if (data) {
          return data as MFAnOTPChangePasswordForLS;
        }
      }
      return null;
    } catch (error) {
      console.error(error);
      this.resetMFAnOTPChangePasswordForLS(email);
      return this.resetStateMFAnOTPChangePasswordForLS();
    }
  }

  checkEmailExist(
    email: string,
  ): Observable<ResponseDataType<CheckEmailOutput>> {
    const params: CheckEmailInput = { email };
    return this.generalService.postData(
      params,
      API.CHECK,
      API.BASE_URL,
      this.menuId,
    );
  }
}
